import { Component, HostListener, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AccountInfo } from '@azure/msal-common';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subscription, filter, take, timer } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { UserService } from './shared/services/user.service';
import { ConfirmationDialogComponent } from './shared/components/confirmation-dialog/confirmation-dialog.component';
import { AllAdminService } from './shared/services/all-admin.service';
import { SubmitAuthService } from './shared/services/submit-auth.service';
import { CustomerAuthService } from './features/customer-submission/services/customer-auth.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  public countDown = Subscription.EMPTY;
  public counter: number;
  public tick = 1000;
  public popupIndication = false;
  private userAccount: AccountInfo | null;
  private defaultScreenLogout: number;
  private defaultWarningTime = 120;
  location: any;

  constructor(
    public userService: UserService,
    public authService: MsalService,
    public msalBroadCastService: MsalBroadcastService,
    private dialog: MatDialog,
    private adminService: AllAdminService,
    private submitAuthService: SubmitAuthService,
    private customerAuthService: CustomerAuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
  ) {
    this.checkLocalStorageUserAndRole();
    localStorage.removeItem('reviewForm');
  }

  ngOnInit() {
    this.customerCallService();
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe(() => {
      const routeTitle = this.getRouteTitle(this.activatedRoute);
      this.titleService.setTitle(routeTitle || 'Geotherm GeoApp'); // Fallback to default title if no title found
    });
    this.countDown = timer(0, this.tick).subscribe(() => {
      if (this.counter > 0) {
        this.counter -= 1;
        sessionStorage.setItem('logout-counter', this.counter.toString());
        if (this.counter === 0) {
          if (this.popupIndication === true) {
            this.logOut();
          } else if (this.userAccount) {
            if (!window.location.pathname.includes('/in-house-dashboard')) {
              // Disabling Auto logoff for TV dashboard screen
              this.openLogoutConfirmationDialog();
              this.resetCounter(this.defaultScreenLogout);
            }
          }
        }
      }
    });
  }

  getRouteTitle(route: ActivatedRoute): string | null {
    if (route.firstChild) {
      return this.getRouteTitle(route.firstChild);
    }
    return route.snapshot.data['title'];
  }

  customerCallService() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const currentUrl = this.router.url;
      // let adminCalling = false;
      if (
        !currentUrl.includes('/customer')
        && !currentUrl.includes('/review')
        && !currentUrl.includes('/sampleform/')
      ) {
        // Team Don't remove the condition if you remove the condition its affect customer way
        // Execute the code only if the URL does not contain /customer, /review, or /sampleform
        this.adminService.getAdmin().subscribe({
          next: (responseList) => {
            const adminObject = responseList.find((item: any) => item.key === 'defaultscreenlogout');
            this.adminService.updateConfig(responseList);
            if (adminObject) {
              this.counter = adminObject.value * 60;
              this.defaultScreenLogout = adminObject.value * 60;
            } else {
            }
          },
        });
      }
    });
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.resetCounter(this.defaultScreenLogout);
  }

  @HostListener('window:blur')
  onBlur(): void {
    // this.resetCounter(0);
  }

  @HostListener('mouseup', ['$event'])
  onMouseup(event: MouseEvent) {
    if (event) {
      this.resetCounter();
    }
  }

  @HostListener('mousemove', ['$event'])
  onMousemove(event: MouseEvent) {
    if (event) {
      this.resetCounter();
    }
  }

  @HostListener('keyup', ['$event'])
  onKeyUp(event: any) {
    if (event) {
      this.resetCounter();
    }
  }

  scrolling: boolean;
  @HostListener('window:scroll', ['$event']) onScrollEvent($event: any) {
    if ($event) {
      this.resetCounter();
    }
  }

  resetCounter(counterTime: number = this.defaultScreenLogout) {
    this.counter = counterTime || this.defaultScreenLogout;
  }

  openLogoutConfirmationDialog() {
    this.popupIndication = true;
    this.resetCounter(this.defaultScreenLogout);
    this.dialog
      .open(ConfirmationDialogComponent, {
        panelClass: 'confirmation-dialog',
        data: {
          title: 'User Inactivity - Logout',
          content:
            'Please save your work as you will soon be logged out due to inactivity. Do you wish to Continue or logoff?',
          cancelText: 'Stay Logged In',
          continueText: 'Logoff',
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe((response?: string) => {
        this.popupIndication = false;
        if (response === 'NO') {
          this.resetCounter();
        } else if (response === 'close') {
          this.resetCounter();
        } else {
          this.logOut();
        }
      });
  }

  checkLocalStorageUserAndRole() {
    this.msalBroadCastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS))
      .subscribe({
        next: (result: any) => {
          const account = result?.payload?.account;
          if (account && this.isGeoThermUser(account.username)) {
            this.authService.instance.setActiveAccount(account);
            this.userService.userAccount.next(account);
            this.userService.userRole.next(account.idTokenClaims?.roles || null);
            this.userAccount = account;
          } else {
            this.authService.logoutRedirect();
          }
        },
        error: () => {},
      });
    // MSAL encrypts its localStorage cache, so the signed-in account is read through MSAL once it has handled any login redirect
    this.msalBroadCastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None), take(1))
      .subscribe(() => {
        const account = this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()[0];
        if (account) {
          this.authService.instance.setActiveAccount(account);
          this.userService.userAccount.next(account);
          this.userService.userRole.next((account.idTokenClaims?.roles as string[] | undefined) || null);
          this.userAccount = account;
          this.customerAuthService.setCanSubmit(true);
          localStorage.removeItem('jwtToken');
        }
        if (!this.userAccount) {
          const token = localStorage.getItem('jwtToken');
          if (token) {
            this.customerAuthService?.setUserLoggedIn(true);
            // this.customerAuthService.validateToken(token)?.subscribe({
            //   next: () => {
            //     this.customerAuthService.setUserLoggedIn(true);
            //   },
            //   error: () => {
            //     this.customerAuthService.setUserLoggedIn(false);
            //   },
            // });
          }
        }
      });
  }

  isGeoThermUser(userName: string): boolean {
    return true;
    // return userName.indexOf('geothermusa.com') > 1;
  }

  logOut() {
    sessionStorage.clear();
    sessionStorage.removeItem('logout-counter');
    sessionStorage.removeItem('Refresh-counter');
    this.router
      .navigateByUrl('dashboard')
      .then(() => {
        this.authService.logoutRedirect();
      })
      .finally(() => { });
  }
}
